import { CM_PAYMENT_STATUSES, cmDeliverablePaymentStatus, emptyCmDeliverable } from '@lib/cmServices';

import type { CmDeliverable, CmDeliverablesEditorProps } from '@feature/contract-mgmt/types/contract-mgmt-components';

function cellInput(
  value: unknown,
  onChange: (v: string) => void,
  type: 'text' | 'number' | 'date' | 'textarea' = 'text',
  readOnly = false,
) {
  if (readOnly) {
    const display = value == null || value === '' ? '—' : String(value);
    return <span className="t-muted">{display}</span>;
  }
  if (type === 'textarea') {
    return (
      <textarea
        className="cm-del-cell-input cm-del-cell-textarea"
        value={String(value ?? '')}
        onChange={(e) => onChange(e.target.value)}
        rows={2}
      />
    );
  }
  return (
    <input
      className={`cm-del-cell-input${type === 'date' ? ' cm-del-cell-date' : ''}`}
      type={type}
      value={value == null ? '' : String(value)}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

export default function CmDeliverablesEditor({ deliverables = [], onChange, readOnly = false }: CmDeliverablesEditorProps) {
  const rows = deliverables.length ? deliverables : [];

  const updateRow = (idx: number, patch: Partial<CmDeliverable>) => {
    onChange(rows.map((row, i) => (i === idx ? { ...row, ...patch } : row)));
  };

  const addRow = () => {
    onChange([...rows, emptyCmDeliverable(rows.length + 1) as CmDeliverable]);
  };

  const removeRow = (idx: number) => {
    onChange(rows.filter((_, i) => i !== idx).map((row, i) => ({ ...row, sr: i + 1 })));
  };

  return (
    <div className="cm-deliverables-editor">
      <div className="cm-form-sec-title">Deliverables</div>
      <p className="cm-deliverables-note">Payment status is tracked per deliverable. Use Deliverable Status for free-text progress notes.</p>
      <div className="tbl-wrap cm-deliverables-wrap">
        <table className="cm-deliverables-table">
          <colgroup>
            <col className="cm-del-col-sr" />
            <col className="cm-del-col-name" />
            <col className="cm-del-col-timeline" />
            <col className="cm-del-col-date" />
            <col className="cm-del-col-date" />
            <col className="cm-del-col-date" />
            <col className="cm-del-col-amt" />
            <col className="cm-del-col-amt" />
            <col className="cm-del-col-amt" />
            <col className="cm-del-col-pay" />
            <col className="cm-del-col-status" />
            <col className="cm-del-col-remarks" />
            {!readOnly && <col className="cm-del-col-action" />}
          </colgroup>
          <thead>
            <tr>
              <th className="cm-del-col-sr t-col-center">Sr</th>
              <th className="cm-del-col-name" title="Name of Deliverable">Deliverable</th>
              <th className="cm-del-col-timeline t-col-center">Timeline</th>
              <th className="cm-del-col-date t-col-center" title="Submission Date">Submitted</th>
              <th className="cm-del-col-date t-col-center" title="Actual Submission">Actual</th>
              <th className="cm-del-col-date t-col-center" title="Date of Approval">Approved</th>
              <th className="cm-del-col-amt t-col-center">Amount</th>
              <th className="cm-del-col-amt t-col-center" title="Provisional Sum">Prov. Sum</th>
              <th className="cm-del-col-amt t-col-center" title="Contingency Amount">Contingency</th>
              <th className="cm-del-col-pay t-col-center" title="Payment Status">Payment</th>
              <th className="cm-del-col-status t-col-center" title="Deliverable Status">Del. Status</th>
              <th className="cm-del-col-remarks">Remarks</th>
              {!readOnly && <th className="cm-del-col-action t-col-center">Action</th>}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={readOnly ? 12 : 13} className="t-muted cm-del-empty" >
                  No deliverables added yet.
                </td>
              </tr>
            ) : rows.map((row, idx) => (
              <tr key={`del-${idx}-${row.sr}`}>
                <td className="cm-del-col-sr t-col-center t-muted">{row.sr}</td>
                <td className="cm-del-col-name">{cellInput(row.name, (v) => updateRow(idx, { name: v }), 'text', readOnly)}</td>
                <td className="cm-del-col-timeline">{cellInput(row.timeline, (v) => updateRow(idx, { timeline: v }), 'text', readOnly)}</td>
                <td className="cm-del-col-date">{cellInput(row.submissionDate, (v) => updateRow(idx, { submissionDate: v }), 'date', readOnly)}</td>
                <td className="cm-del-col-date">{cellInput(row.actualSubmission, (v) => updateRow(idx, { actualSubmission: v }), 'date', readOnly)}</td>
                <td className="cm-del-col-date">{cellInput(row.approvalDate, (v) => updateRow(idx, { approvalDate: v }), 'date', readOnly)}</td>
                <td className="cm-del-col-amt">{cellInput(row.amount, (v) => updateRow(idx, { amount: v }), 'number', readOnly)}</td>
                <td className="cm-del-col-amt">{cellInput(row.provisionalSum, (v) => updateRow(idx, { provisionalSum: v }), 'number', readOnly)}</td>
                <td className="cm-del-col-amt">{cellInput(row.contingencyAmount, (v) => updateRow(idx, { contingencyAmount: v }), 'number', readOnly)}</td>
                <td className="cm-del-col-pay">
                  {readOnly ? (
                    <span>{cmDeliverablePaymentStatus(row)}</span>
                  ) : (
                    <select
                      className="cm-del-cell-input cm-del-cell-select"
                      value={cmDeliverablePaymentStatus(row)}
                      onChange={(e) => updateRow(idx, { paymentStatus: e.target.value, payment: e.target.value })}
                    >
                      {CM_PAYMENT_STATUSES.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                      {!CM_PAYMENT_STATUSES.includes(cmDeliverablePaymentStatus(row) as typeof CM_PAYMENT_STATUSES[number])
                        && cmDeliverablePaymentStatus(row) !== 'Pending' && (
                        <option value={cmDeliverablePaymentStatus(row)}>{cmDeliverablePaymentStatus(row)}</option>
                      )}
                    </select>
                  )}
                </td>
                <td className="cm-del-col-status">{cellInput(row.status, (v) => updateRow(idx, { status: v }), 'text', readOnly)}</td>
                <td className="cm-del-col-remarks">{cellInput(row.remarks, (v) => updateRow(idx, { remarks: v }), 'textarea', readOnly)}</td>
                {!readOnly && (
                  <td className="cm-del-col-action t-col-center">
                    <button type="button" className="tr-delete-btn" onClick={() => removeRow(idx)}>Remove</button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!readOnly && (
        <button type="button" className="btn-listing-bar cm-deliverables-add-btn" onClick={addRow}>
          Add Deliverable
        </button>
      )}
    </div>
  );
}
